import { useState } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { billingFieldErrors } from '../utils/credit-card-form-validation';
import type { AddonCardEntry } from './use-addon-card-entry';
import { useBillingEditGuard } from './use-billing-edit-guard';

export type AddonPayment =
  | { method: 'saved-card' }
  | { method: 'new-card'; paymentMethodId: string; name: string; address: CreditCardFormAddress };

const CARD_INCOMPLETE = 'Enter your card number, expiry date, and CVC.';
const CARD_FAILED = "Your card couldn't be checked. Try again.";
const TEST_PAYMENT_METHOD_ID = 'pm_test_default';

function isNewCardReady(entry: AddonCardEntry): boolean {
  const fieldErrors = Object.keys(billingFieldErrors(entry.name, entry.address));
  if (isCardComplete(entry) && fieldErrors.length === 0) return true;
  entry.onOpenForm();
  entry.onTouchFields(fieldErrors);
  return false;
}

function isCardComplete(entry: AddonCardEntry): boolean {
  return IS_STRIPE_TEST_MODE || entry.isCardComplete;
}

type PaymentMethodOutcome = { paymentMethodId?: string; error?: string };

async function createCardPaymentMethod(
  stripe: Stripe | null,
  card: StripeCardNumberElement | null,
  entry: AddonCardEntry,
): Promise<PaymentMethodOutcome> {
  if (stripe == null || card == null) return { error: CARD_FAILED };
  const result = await stripe.createPaymentMethod({
    type: 'card',
    card,
    billing_details: { name: entry.name, address: { postal_code: entry.address.zipCode } },
  });
  if (result.error == null) return { paymentMethodId: result.paymentMethod.id };
  return { error: result.error.type === 'card_error' ? (result.error.message ?? CARD_FAILED) : CARD_FAILED };
}

export type AddonPaymentStep = {
  cardError: string | undefined;
  blockReason: string;
  isBlocked: boolean;
  resolve: () => Promise<AddonPayment | null>;
};

export function useAddonPayment(entry: AddonCardEntry): AddonPaymentStep {
  const stripe = useStripe();
  const elements = useElements();
  const editGuard = useBillingEditGuard();
  const [isCardErrorShown, setIsCardErrorShown] = useState(false);
  const [stripeError, setStripeError] = useState<string>();
  const cardError = stripeError ?? (isCardErrorShown && !isCardComplete(entry) ? CARD_INCOMPLETE : undefined);

  const isStripeBlocked = entry.stripeLoad.isError || (!entry.useSavedCard && entry.stripeLoad.isLoading);

  const resolve = async (): Promise<AddonPayment | null> => {
    if (entry.useSavedCard) return { method: 'saved-card' };
    setIsCardErrorShown(true);
    setStripeError(undefined);
    if (!isNewCardReady(entry)) return null;

    const outcome = IS_STRIPE_TEST_MODE
      ? { paymentMethodId: TEST_PAYMENT_METHOD_ID }
      : await createCardPaymentMethod(stripe, elements?.getElement(CardNumberElement) ?? null, entry);
    setStripeError(outcome.error);
    if (outcome.paymentMethodId == null) return null;
    return { method: 'new-card', paymentMethodId: outcome.paymentMethodId, name: entry.name, address: entry.address };
  };

  return {
    cardError,
    blockReason: editGuard.isDisabled ? editGuard.disabledMessage : '',
    isBlocked: editGuard.isDisabled || isStripeBlocked,
    resolve,
  };
}
